<template>
  <div
    class="base-tags"
    :style="styles"
  >
    <div class="base-tags--inputs">
      <BaseInput
        v-if="selectableValues === null && tagInput"
        v-model="newTag"
        :label="label"
        :name="name"
        :outlined="outlined"
        :rounded="rounded"
        :size="size"
        :type="type"
        :error="errorMessage"
        @enter="onEnter"
        @update="onInput($event)"
      />
      <BaseSelect
        v-if="selectableValues && tagSelect"
        :label="label"
        :name="name"
        :items="selectValues"
        :outlined="outlined"
        :rounded="rounded"
        :size="size"
        :error="errorMessage"
        @update="onSelect($event)"
      />
      <BaseFileInput
        v-if="tagUpload"
        v-model="file"
        :label="fileLabel"
        :default-icon="false"
        :disabled="uploadPending"
        outlined
        rounded
        accept=".csv"
        :error="errorMessageFile"
        @update="errorMessageFile = ''"
      >
        <template #iconLeft>
          <BaseBadge
            filled="company-main-primary"
            :size="54"
            pill
          >
            <BaseIcon
              icon="far fa-upload"
              :style="{
                fontSize: '20px',
              }"
            />
          </BaseBadge>
        </template>
      </BaseFileInput>
    </div>
    <div :class="{ tags: true, 'tags--colored': colored }">
      <div
        v-for="(tag, inx) in tags"
        :key="`${name}-${inx}`"
        class="tag"
        @click="removeTag(tag)"
        @keydown.enter="removeTag(tag)"
      >
        <span>{{ getTagLabel(tag) }}<i
          v-if="clearable"
          class="far fa-times"
        /></span>
      </div>
      <div
        v-if="(value || []).length === 0 && tagPlaceholder"
        class="tag disabled"
      >
        <span>{{ tagPlaceholder }}</span>
      </div>
    </div>
    <div
      v-if="showStandaloneError"
      class="input-hint"
    >
      <div
        class="input-hint--error"
        role="alert"
        aria-live="assertive"
      >
        <i
          class="fa-regular fa-circle-exclamation"
          aria-hidden="true"
        />
        <span v-html="errorMessage" />
      </div>
    </div>
  </div>
</template>

<script>
import Papa from 'papaparse';
import _ from 'lodash';

export default {
  name: 'BaseTags',

  props: {
    label: {
      type: String,
      default: '',
    },
    fileLabel: {
      type: String,
      default: 'Liste auswählen',
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      type: [Array, Object, String, Number],
      default: () => ([]),
    },
    selectableValues: {
      type: Array,
      required: false,
      default: () => null,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      validator(value) {
        return ['', 's', 'xs'].indexOf(value) !== -1;
      },
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: () => 'text',
      validator: (value) => ['text', 'number', 'email', 'phone'].includes(value),
    },
    error: {
      type: [String, Array],
      required: false,
      default: () => null,
    },
    validator: {
      type: Function,
      required: false,
      default: () => null,
    },
    tagPlaceholder: {
      type: String,
      required: false,
      default: () => null,
    },
    clearOnSelectablesChange: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    clearIfInvalid: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    tagUpload: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxTags: {
      type: Number,
      required: false,
      default: 500,
    },
    tagInput: {
      type: Boolean,
      required: false,
      default: true,
    },
    tagSelect: {
      type: Boolean,
      required: false,
      default: true,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    colored: {
      type: [Boolean, String],
      required: false,
      default: true,
    },
    direction: {
      type: String,
      required: false,
      default: () => 'column',
      validator: (value) => ['row', 'column'].includes(value),
    },
    columnGap: {
      type: String,
      required: false,
      default: () => '2rem',
    },
    rowGap: {
      type: String,
      required: false,
      default: () => '10px',
    },
  },

  data() {
    return {
      newTag: '',
      componentError: '',
      errorMessageFile: '',
      selectValue: null,
      uploadPending: false,
      file: [],
    };
  },

  computed: {
    styles() {
      let filled = '';
      if (_.isBoolean(this.colored)) {
        filled = 'var(--company-main-primary)';
      } else if (_.isString(this.colored)) {
        filled = this.colored.includes('#')
          ? this.colored
          : `var(--${this.colored})`;
      }

      return {
        '--base-tags--color': filled,
      };
    },
    tags: {
      get() {
        if (!Array.isArray(this.value) && ['string', 'number'].indexOf(typeof this.value) >= 0) {
          return [
            this.value,
          ];
        } if (!Array.isArray(this.value)) {
          return [];
        }

        return this.value;
      },
      set(newValue) {
        this.errorMessageFile = '';
        this.$emit('input', newValue);
      },
    },
    getDirection() {
      return this.direction === 'row' ? 2 : 1;
    },
    tagMargin() {
      return this.direction === 'row' ? '0.27rem 0 0' : '0 -5px 0 0';
    },
    selectValues() {
      const notAlreadySelected = (this.selectableValues || []).filter((selectable) => {
        if (Array.isArray(this.value)) {
          return this.value.findIndex((selectedValue) => {
            if (!Number.isNaN(Number(selectedValue)) && !Number.isNaN(Number(selectable.value))) {
              return parseFloat(selectedValue) === parseFloat(selectable.value);
            }

            return selectedValue === selectable.value;
          }) === -1;
        }

        return true;
      });

      const result = [];
      if (this.tagPlaceholder) {
        result.push({ label: this.tagPlaceholder, value: null });
      }
      result.push(...notAlreadySelected);

      return result;
    },
    errorMessage() {
      return this.error || this.componentError || null;
    },
    // BaseTags has no sub-input that renders the `error` prop when it is used as a
    // pure tag display (tag-input=false, no selectableValues, no error-bearing input).
    // In that case render our own hint so the bound error is still shown.
    showStandaloneError() {
      const baseInputActive = this.selectableValues === null && this.tagInput;
      const baseSelectActive = !!this.selectableValues && this.tagSelect;
      return !!this.errorMessage && !baseInputActive && !baseSelectActive;
    },
  },

  watch: {
    selectableValues(value) {
      if (this.clearOnSelectablesChange) {
        this.$emit('input', []);
      } else if (this.clearIfInvalid) {
        const hasInvalidValue = this.value.some((val) => !value.map((v) => v.value).includes(val));

        if (hasInvalidValue) {
          this.$emit('input', []);
        }
      }
    },
    file() {
      this.uploadPending = true;
      // Code is highly hacky, but needed to prevent click events on the file input
      // from being triggered when the file is parsed in FE
      // (maybe parsing holds the script execution which prevents reactivity)
      setTimeout(() => {
        console.log('parse file');
        this.parseFile()
          .catch((error) => {
            this.errorMessageFile = error.message;
          })
          .finally(() => {
            setTimeout(() => {
              this.uploadPending = false;
            }, 500);
          });
      }, 500);
    },
  },

  methods: {
    async parseFile() {
      return new Promise((resolve, reject) => {
        if (this.file.length) {
          Papa.parse(this.file[0], {
            skipEmptyLines: 'greedy',
            complete: (results) => {
              const multirow = results.data.map(
                (row) => row.every(this.validEntry),
              ).filter((row) => row).length > 1;
              const data = multirow
                ? results.data.map((row) => row[0]).filter(this.validEntry)
                : results.data[0].map((cell) => cell.trim()).filter(this.validEntry);

              if (data.length > this.maxTags) {
                this.file = [];
                reject(new Error(`Maximal ${this.maxTags} Einträge erlaubt`));
                return;
              }

              this.tags = [...this.value, ...data];
              this.$emit('input', data);
              this.$emit('upload', data);
              resolve();
            },
          });
        } else {
          resolve();
        }
      });
    },
    validEntry(callback) {
      return callback !== null && callback !== '';
    },
    onEnter() {
      if (this.newTag.trim() !== '') {
        if (this.value.indexOf(this.newTag) >= 0) {
          this.componentError = 'Eintrag bereits vorhanden';
        } else {
          let error = '';

          if (typeof this.validator === 'function') {
            error = `${this.validator(this.newTag) || ''}`;
          }

          if (error.length) {
            this.componentError = error;
          } else {
            this.tags = [...this.value, this.newTag];
            this.$emit('add', this.newTag);
            this.newTag = '';
          }
        }
      }
    },
    onInput(value) {
      this.componentError = '';
      this.errorMessageFile = '';
      this.$emit('update', value);
    },
    onSelect(value) {
      this.componentError = null;
      this.errorMessageFile = null;
      if (value !== null) {
        const selectedTag = `${value === 0 ? '0' : (value || '')}`.trim();

        if (selectedTag !== '') {
          if (this.value.indexOf(selectedTag) >= 0) {
            this.componentError = 'Eintrag bereits vorhanden';
          } else {
            let error = '';

            if (typeof this.validator === 'function') {
              error = `${this.validator(selectedTag) || ''}`;
            }

            if (error.length) {
              this.componentError = error;
            } else {
              this.tags = [...this.value, selectedTag];
              this.$emit('add', selectedTag);
            }
          }
        }

        this.selectValue = null;
      }
    },
    getTagLabel(tag) {
      return Object.values(this.selectableValues || {})
        .find((val) => this.validateValue(val.value, tag))?.label || tag;
    },
    validateValue(value, tag) {
      if (!Number.isNaN(Number(value))) {
        return parseFloat(value) === parseFloat(tag);
      }
      return value === tag;
    },
    removeTag(tag) {
      this.tags = [...this.value].filter((t) => t !== tag);
      this.$emit('remove', tag);
    },
  },
};
</script>

<style lang="scss" scoped>
.base-tags {
  display: grid;
  grid-template-columns: repeat(v-bind(getDirection), 1fr);
  column-gap: v-bind(columnGap);
  row-gap: v-bind(rowGap);
  align-items: flex-start;

  .input-hint {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    padding: 0.25em 0;

    &--error {
      display: flex;
      align-items: center;
      gap: 0.4em;
      font-size: 12px;
      line-height: 1.25;
      color: var(--base-input--error-color);
    }
  }

  &--inputs {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
  }
  .tags {
    margin: v-bind(tagMargin);

    .tag {
      display: inline-block;
      padding: 5px;

      > span {
        padding: .33333em .66666em;
        display: inline-block;
        font-size: 16px;
        border-radius: 999px;
        cursor: pointer;
        font-weight: 300;
        color: #3D3D3D;
        background-color: #EDEDED;

        > i, > svg {
          margin-left: .5em;
        }
      }

      &.disabled {
        > span {
          background-color: #FFFFFF !important;
          color: #3D3D3D !important;
          border: 1px solid #3D3D3D !important;
          pointer-events: none;
          cursor: default;
        }
      }
    }

    &--colored {
      .tag > span {
        color: #FFFFFF;
        background-color: var(--base-tags--color);

        &:hover {
          color: #3D3D3D;
          background-color: #EDEDED;
        }
      }
    }
  }
}
</style>
